import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/logauditoria.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const LogAuditoriaManager = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    tabela_afetada: '',
    id_registro: '',
    tipo_evento: '',
    dados_anteriores: '',
    data_evento: '',
    usuario_evento: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/log-auditoria');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
  }, [verificarAPI]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'log de auditoria', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/log-auditoria/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'log de auditoria', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'log de auditoria');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'log de auditoria', { id });
    try {
      const response = await api.delete(`/log-auditoria/${id}`);
      logarSucesso('Excluir', 'log de auditoria', response.data);
      const novosDados = dados.filter(item => item.id_log !== id);
      setDados(novosDados);
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'log de auditoria');
      mostrarMensagem('error', mensagemErro);
    }
  }, [dados, mostrarMensagem]);

  const colunas = useMemo(() => [
    {
      title: 'Salvar',
      field: 'salvar',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
      cellClick: (e, cell) => {
        const data = cell.getRow().getData();
        atualizarRegistro(data.id_log, data);
      },
      headerSort: false
    },
    {
      title: 'Excluir',
      field: 'excluir',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
      cellClick: (e, cell) => {
        const data = cell.getRow().getData();
        if (window.confirm(`Deseja realmente excluir o registro "${data.id_log}"?`)) {
          excluirRegistro(data.id_log);
        }
      },
      headerSort: false
    },
    {
      title: 'ID Log',
      field: 'id_log',
      width: 100,
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar ID...',
      sorter: 'number',
      editor: 'input'
    },
    {
      title: 'Tabela Afetada',
      field: 'tabela_afetada',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar tabela...',
      sorter: 'string',
      editor: 'input'
    },
    {
      title: 'ID Registro',
      field: 'id_registro',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar ID...',
      sorter: 'number',
      editor: 'input'
    },
    {
        title: 'Tipo Evento',
        field: 'tipo_evento',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar evento...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Dados Anteriores',
        field: 'dados_anteriores',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar dados...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Data Evento',
        field: 'data_evento',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar data...',
        sorter: 'string',
        formatter: (cell) => {
          const valor = cell.getValue();
          if (!valor) return '';
          const data = new Date(valor);
          return data.toLocaleDateString('pt-BR');
        },
        editor: (cell, onRendered, success, cancel) => {
          const valor = cell.getValue();
          let valorFormatado = '';
          if (valor) {
            const data = new Date(valor);
            const dia = String(data.getDate()).padStart(2, '0');
            const mes = String(data.getMonth() + 1).padStart(2, '0');
            const ano = data.getFullYear();
            valorFormatado = `${dia}/${mes}/${ano}`;
          }
          const input = document.createElement('input');
          input.setAttribute('type', 'text');
          input.setAttribute('placeholder', 'DD/MM/AAAA');
          input.style.width = '100%';
          input.value = valorFormatado;
          
          onRendered(() => {
            input.focus();
            input.select();
          });
          
          input.addEventListener('blur', () => {
            const valorDigitado = input.value;
            if (valorDigitado) {
              const partes = valorDigitado.split('/');
              if (partes.length === 3) {
                const dataISO = `${partes[2]}-${partes[1]}-${partes[0]}`;
                success(dataISO);
              } else {
                success(valorDigitado);
              }
            } else {
              success(null);
            }
          });
          
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              const valorDigitado = input.value;
              if (valorDigitado) {
                const partes = valorDigitado.split('/');
                if (partes.length === 3) {
                  const dataISO = `${partes[2]}-${partes[1]}-${partes[0]}`;
                  success(dataISO);
                } else {
                  success(valorDigitado);
                }
              } else {
                success(null);
              }
            }
            if (e.key === 'Escape') {
              cancel();
            }
          });
          
          return input;
        }
      },
      {
        title: 'Usuário Evento',
        field: 'usuario_evento',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar usuário...',
        sorter: 'string',
        editor: 'input'
      }
  ], [atualizarRegistro, excluirRegistro]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/log-auditoria');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      layout: 'fitData',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_log'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { tabela_afetada, id_registro, tipo_evento, dados_anteriores, data_evento, usuario_evento } = formData;

    const camposFaltando = [];
    if (!tabela_afetada) camposFaltando.push('Tabela Afetada');
    if (!id_registro) camposFaltando.push('ID Registro');
    if (!tipo_evento) camposFaltando.push('Tipo Evento');
    if (!dados_anteriores) camposFaltando.push('Dados Anteriores');
    if (!data_evento) camposFaltando.push('Data Evento');
    if (!usuario_evento) camposFaltando.push('Usuário Evento');
    
    if (camposFaltando.length > 0) {
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposFaltando.join(', ')}`);
      return;
    }

    const dataToSend = {
      tabela_afetada,
      id_registro,
      tipo_evento,
      dados_anteriores,
      data_evento: data_evento === '' ? null : data_evento,
      usuario_evento
    };

    logarRequisicao('Adicionar', 'log de auditoria', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/log-auditoria', dataToSend);
      logarSucesso('Adicionar', 'log de auditoria', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ tabela_afetada: '', id_registro: '', tipo_evento: '', dados_anteriores: '', data_evento: '', usuario_evento: '' });
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'log de auditoria');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'log_auditoria.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => window.history.back()}><i class="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Log de Auditoria</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_log_auditoria</p>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="tabela_afetada">Tabela Afetada:<span style={{color: 'red', marginLeft: '2px'}}>*</span></label>
            <input type="text" id="tabela_afetada" name="tabela_afetada" value={formData.tabela_afetada} onChange={handleInputChange} placeholder="Ex: tb_escola" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="id_registro">ID Registro:<span style={{color: 'red', marginLeft: '2px'}}>*</span></label>
            <input type="text" id="id_registro" name="id_registro" value={formData.id_registro} onChange={handleInputChange} placeholder="Ex: 1" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="tipo_evento">Tipo Evento:<span style={{color: 'red', marginLeft: '2px'}}>*</span></label>
            <input type="text" id="tipo_evento" name="tipo_evento" value={formData.tipo_evento} onChange={handleInputChange} placeholder="Ex: INSERT" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="dados_anteriores">Dados Anteriores:<span style={{color: 'red', marginLeft: '2px'}}>*</span></label>
            <input type="text" id="dados_anteriores" name="dados_anteriores" value={formData.dados_anteriores} onChange={handleInputChange} placeholder='{"coluna": "valor"}' disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="data_evento">Data Evento:<span style={{color: 'red', marginLeft: '2px'}}>*</span></label>
            <input type="date" id="data_evento" name="data_evento" value={formData.data_evento} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="usuario_evento">Usuário Evento:<span style={{color: 'red', marginLeft: '2px'}}>*</span></label>
            <input type="text" id="usuario_evento" name="usuario_evento" value={formData.usuario_evento} onChange={handleInputChange} placeholder="Ex: admin" disabled={loading || apiStatus !== 'connected'} />
          </div>
          
          <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
            {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
          </button>
          <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
            {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
          </button>
          <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📅 Exportar CSV</button>
          
          <div className="info-campos-obrigatorios">
            <i className="fas fa-info-circle"></i>
            <span>* indica que os campos são obrigatorios.</span>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default LogAuditoriaManager;
